import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useClipboard — Excel-style copy/paste for the grid (Edit mode only).
 *
 * Cells
 *   Ctrl/Cmd+C copies the selection (single cell or range) to the system
 *   clipboard as tab-separated text — the format Excel uses — and keeps an
 *   internal copy that ALSO carries each cell's formatting (font, fill,
 *   borders, alignment, date format, rich text). Ctrl/Cmd+V pastes values
 *   AND formatting when the clipboard still holds what was copied here
 *   (QA: "font style is not the same after copy/paste"); text copied from
 *   another application pastes as plain values.
 *
 *   The copied range is outlined with the animated "marching ants" border
 *   until Escape is pressed or a cell is edited (`copyRange`).
 *
 * Charts / images
 *   When a chart or image is selected (click it in Edit mode), Ctrl+C copies
 *   the object and Ctrl+V pastes a duplicate at the active cell
 *   (`copyObject` / `pasteObject` supplied by the viewer).
 *
 * Copy and paste only work while Edit mode is ON (QA: cells were marked as
 * copied when Ctrl+C was pressed outside Edit mode). Keystrokes inside real
 * text fields (formula bar, in-cell editor) keep native clipboard handling.
 */
// Module-level internal clipboard: RPDs run under document-protection
// (copy guards) and the async Clipboard API needs a secure context +
// permission — either can silently break the system clipboard. Copy/paste
// inside the viewer must always work, so the copied content is kept here.
//   { kind: "cells", text, cells: [[cellInfo]] }  |  { kind: "object" }
let internalClipboard = null;

// Compare clipboard texts ignoring line-ending differences / trailing newline.
const normText = (t) => String(t == null ? "" : t).replace(/\r\n?/g, "\n").replace(/\n+$/, "");

export default function useClipboard({
  enabled, activeSheet, selectedCell, normalizedSelection, normalizedRows, commitCells,
  selectionType, setSelection, setSelectedCell, setSelectionType,
  cellStyles, cellRichHtml, dateFormats, numFmts,
  getSelectedObject, copyObject, pasteObject,
}) {
  // Range currently marked as "copied" (drawn as a marching-ants border).
  const [copyRange, setCopyRange] = useState(null);
  const clearCopyRange = useCallback(() => setCopyRange(null), []);

  // Live state for the (stable) window listener.
  const stateRef = useRef({});
  stateRef.current = {
    activeSheet, selectedCell, normalizedSelection, normalizedRows, commitCells,
    selectionType, setSelection, setSelectedCell, setSelectionType,
    cellStyles, cellRichHtml, dateFormats, numFmts,
    getSelectedObject, copyObject, pasteObject,
  };

  // Leaving Edit mode drops the copy marker.
  useEffect(() => { if (!enabled) setCopyRange(null); }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const writeClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (_) { /* fall through to execCommand */ }
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) { /* clipboard unavailable */ }
    };

    const copyCells = (s) => {
      const sel = s.normalizedSelection ||
        { r1: s.selectedCell.row, r2: s.selectedCell.row, c1: s.selectedCell.col, c2: s.selectedCell.col };
      const lines = [];
      const cells = [];
      for (let r = sel.r1; r <= sel.r2; r += 1) {
        const texts = [];
        const infos = [];
        for (let c = sel.c1; c <= sel.c2; c += 1) {
          const key = `${r}:${c}`;
          const v = s.normalizedRows[r] ? s.normalizedRows[r][c] : "";
          const text = v == null ? "" : String(v);
          texts.push(text);
          const style = s.cellStyles && s.cellStyles[key];
          infos.push({
            text,
            style: style ? { ...style } : null,
            richHtml: (s.cellRichHtml && s.cellRichHtml[key]) || null,
            dateFormat: (s.dateFormats && s.dateFormats[key]) || null,
            numFmt: (s.numFmts && s.numFmts[key]) || null,
            // Source cell in FILE coordinates (edit mode clears sort/filter,
            // so display == file coords) — the saver copies its cell style.
            src: { sheet: s.activeSheet, row: r, col: c },
          });
        }
        lines.push(texts.join("\t"));
        cells.push(infos);
      }
      const text = lines.join("\n");
      internalClipboard = { kind: "cells", text, cells };
      writeClipboard(text);
      setCopyRange({ sheet: s.activeSheet, r1: sel.r1, c1: sel.c1, r2: sel.r2, c2: sel.c2 });
    };

    // Paste a block (2-D array of { text, style?, ... }) Excel-style.
    const pasteBlock = (block) => {
      const st = stateRef.current;
      const bh = block.length;
      const bw = Math.max(1, ...block.map((r) => r.length));

      // The block lands at the top-left of the current selection; when a
      // cell-range selection is an exact multiple of the block's size, the
      // block repeats to fill it (copy one cell, select A1:A10, paste).
      const sel = st.normalizedSelection ||
        { r1: st.selectedCell.row, r2: st.selectedCell.row, c1: st.selectedCell.col, c2: st.selectedCell.col };
      const top = sel.r1;
      const left = sel.c1;
      const selH = sel.r2 - sel.r1 + 1;
      const selW = sel.c2 - sel.c1 + 1;
      const canTile = (st.selectionType || "cell") === "cell" &&
        (selH > bh || selW > bw) && selH % bh === 0 && selW % bw === 0 &&
        selH * selW <= 20000;
      const outH = canTile ? selH : bh;
      const outW = canTile ? selW : bw;

      const edits = [];
      for (let i = 0; i < outH; i += 1) {
        const srcRow = block[i % bh];
        for (let j = 0; j < outW; j += 1) {
          const cell = srcRow[j % bw] || { text: "" };
          const edit = { row: top + i, col: left + j, text: cell.text == null ? "" : cell.text };
          if (cell.formatted) {
            // Formatting travels with the value (Excel's default paste).
            edit.style = cell.style;
            edit.richHtml = cell.richHtml;
            edit.dateFormat = cell.dateFormat;
            edit.numFmt = cell.numFmt;
            edit.styleSource = cell.src;
          }
          edits.push(edit);
        }
      }
      if (!edits.length) return;
      st.commitCells(edits);

      // Leave ALL pasted cells selected, active cell at the top-left.
      const anchor = { row: top, col: left };
      const focus = { row: top + outH - 1, col: left + outW - 1 };
      if (typeof st.setSelectionType === "function") st.setSelectionType("cell");
      if (typeof st.setSelection === "function") st.setSelection({ anchor, focus, dragging: false });
      if (typeof st.setSelectedCell === "function") st.setSelectedCell(anchor);
    };

    const pasteText = (text) => {
      const ic = internalClipboard;
      // Clipboard still holds what was copied in this viewer -> paste the
      // rich copy (values + formatting).
      if (ic && ic.kind === "cells" && (text == null || normText(text) === normText(ic.text))) {
        pasteBlock(ic.cells.map((row) => row.map((c) => ({ ...c, formatted: true }))));
        return;
      }
      if (text == null || text === "") return;
      const lines = String(text).replace(/\r\n?/g, "\n").split("\n");
      if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
      pasteBlock(lines.map((line) => line.split("\t").map((t) => ({ text: t }))));
    };

    const onKeyDown = (e) => {
      // Escape clears the copy marker (Excel behaviour).
      if (e.key === "Escape") { setCopyRange(null); return; }
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      const key = String(e.key).toLowerCase();
      if (key !== "c" && key !== "v") return;
      const tag = String(e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return; // native behaviour
      const s = stateRef.current;

      if (key === "c") {
        e.preventDefault();
        const obj = typeof s.getSelectedObject === "function" ? s.getSelectedObject() : null;
        if (obj && typeof s.copyObject === "function" && s.copyObject()) {
          internalClipboard = { kind: "object" };
          return;
        }
        copyCells(s);
        return;
      }

      // Paste.
      if (typeof s.commitCells !== "function") return;
      e.preventDefault();
      if (internalClipboard && internalClipboard.kind === "object") {
        if (typeof s.pasteObject === "function") s.pasteObject();
        return;
      }
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText()
          .then((text) => pasteText(text || null))
          .catch(() => pasteText(null));
      } else {
        pasteText(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);

  return { copyRange, clearCopyRange };
}
